import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { ScrollReveal, StaggerParent, StaggerItem } from "@/components/shared/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";

const IMAGES = [
  `${process.env.PUBLIC_URL}/images/cases/auto.jpg`,
  `${process.env.PUBLIC_URL}/images/hero.jpg`,
  `${process.env.PUBLIC_URL}/images/cases/food.jpg`,
  `${process.env.PUBLIC_URL}/images/modo/economia.jpg`,
];

export default function VideoFormats() {
  const { t } = useLang();
  return (
    <section
      data-testid="video-section"
      className="relative bg-black text-white py-28 md:py-40 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <ScrollReveal className="lg:col-span-7">
            <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">{t.video.eyebrow}</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight whitespace-pre-line">
              {t.video.title}
            </h2>
          </ScrollReveal>
          <ScrollReveal className="lg:col-span-5 lg:pb-2" delay={0.15}>
            <p className="text-neutral-400 text-lg leading-relaxed max-w-lg">{t.video.body}</p>
          </ScrollReveal>
        </div>

        <StaggerParent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {t.video.items.map((v, i) => (
            <StaggerItem key={v.name}>
              <motion.a
                href={v.comingSoon ? "#" : v.url || "#"}
                target={v.comingSoon ? "_self" : "_blank"}
                rel={v.comingSoon ? undefined : "noopener noreferrer"}
                aria-label={v.comingSoon ? v.name : `${v.name} — YouTube`}
                data-testid={`video-item-${i}`}
                className="group relative overflow-hidden clip-frame aspect-[4/5] cursor-pointer block"
              >
                <img
                  src={IMAGES[i % IMAGES.length]}
                  alt={v.name}
                  className={`w-full h-full object-cover spotlight-img ${v.comingSoon ? "grayscale" : ""}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/20" />

                {v.comingSoon ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="px-4 py-2 bg-[var(--dp-accent)] text-white text-[11px] tracking-[0.22em] uppercase rounded-full">
                      {t.videoStrip?.soon || "Coming soon"}
                    </div>
                  </div>
                ) : (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-white/70 flex items-center justify-center backdrop-blur-sm bg-black/20 group-hover:bg-[var(--dp-accent)] group-hover:border-[var(--dp-accent)] transition-all duration-500">
                      <Play size={22} className="text-white translate-x-0.5" />
                    </div>
                  </motion.div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono-eyebrow text-neutral-300">{v.cat}</p>
                    <p className="font-mono-eyebrow text-[var(--dp-accent)]">{v.stat}</p>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl leading-none tracking-tight mb-2">
                    {v.name}
                  </h3>
                  <p className="text-neutral-400 text-sm max-w-xs">{v.desc}</p>
                </div>
              </motion.a>
            </StaggerItem>
          ))}
        </StaggerParent>
      </div>
    </section>
  );
}
